import { FileWithPreview } from '@tock/coretypes';
import { LoadImageOptions, MetaData } from 'blueimp-load-image';

/** Corrects an image’s orientation (usually during an upload) and then calls the supplied callbacks. */
export function correctImageOrientationWithCallback(
  imageFile: File,
  loadImageOptions: LoadImageOptions,
  onCorrected: (image: Event | HTMLCanvasElement | HTMLImageElement) => void,
  onParseMetadata?: (meta: MetaData) => void
) {
  if (BROWSER) {
    import('blueimp-load-image').then((loadImage) => {
      const isParseMetadata = Boolean(onParseMetadata);
      loadImage.default(
        imageFile,
        (image, metadata) => {
          onCorrected(image);

          if (onParseMetadata && metadata) {
            onParseMetadata(metadata);
          }
        },
        {
          canvas: true,
          orientation: true,
          meta: isParseMetadata,
          ...loadImageOptions,
        }
      );
    });
  }
}

/** A wrapper around `correctImageOrientationWithCallback` that takes an image `File` and returns a promise resolving to the corrected `FileWithPreview`. */
export function correctImageFile(imageFile: File): Promise<FileWithPreview> {
  // Since the conversion from `Image` -> `Blob` involves a callback-based API, we're going to convert that to promises at the end.
  return new Promise((resolve, reject) => {
    correctImageOrientationWithCallback(
      imageFile,
      {
        canvas: true,
        orientation: true,
      },
      (image) => {
        try {
          // Since blueimp returns the canvas element, we have to convert its data back to a `File` that can be passed to `props.onUploadNewImages()`.
          (image as HTMLCanvasElement).toBlob((blob) => {
            if (blob) {
              const correctedFile = new File([blob], imageFile.name, blob) as FileWithPreview;
              correctedFile.preview = (image as HTMLCanvasElement).toDataURL();

              resolve(correctedFile);
            } else {
              reject(new Error('Image contains no/faulty data'));
            }
          }, imageFile.type);
        } catch (e) {
          reject(String(e));
        }
      }
    );
  });
}
